// The sync indicator (design_handoff_round2 §4B): one chip, four states, a light
// (office) and a dark (field apps) look. Built on trackedDb's write log:
//   Live                      connected, nothing waiting
//   Saving {n}                this page has writes in flight for more than a second
//   Offline · {n} on device   no connection; {n} changes kept on this device
//   {n} not saved             the server refused a change (or a replay kept failing)
// Tapping the chip lists what's waiting or failed, with Try again / Discard.

import { isFirebaseConnected, onFirebaseConnectedChange } from './initFirebase';
import { pendingWrites, inFlightWrites, onPendingWritesChange, discardPendingWrite, retryPendingWrites, useMinePendingWrite } from './trackedDb';
import { escapeHTML } from './utilities';

const SAVING_DELAY_MS = 1000;

// What a database path is about, in words, for the list ("customers/abc/Boats/x" → "Customer record").
const PATH_WORDS = [
  [/^jobs?\b|^openJobs|^workOrders?/i, 'Work order'],
  [/^customers\b/i, 'Customer record'],
  [/^inventory\b/i, 'Inventory'],
  [/^receiving\b/i, 'Receiving'],
  [/^mooringDatabase\b/i, 'Mooring record'],
  [/^rentalDatabase\b/i, 'Mooring rental'],
  [/^moorGPS\b/i, 'Mooring position'],
  [/^launchDispatch\b/i, 'Launch dispatch'],
  [/^batter/i, 'Battery record'],
  [/^haulLaunch/i, 'Haul / launch'],
  [/^schedule|^techSchedule|^techTimeOff/i, 'Schedule'],
  [/^labor|^dailyLog/i, 'Labor / daily log'],
  [/^fuel/i, 'Fuel'],
  [/^maps\b|^yardGeo/i, 'Yard map'],
  [/^soundingSurvey/i, 'Depth survey'],
];
function describe(path) {
  const hit = PATH_WORDS.find(([re]) => re.test(path || ''));
  return hit ? hit[1] : 'Change';
}
function ago(ms) {
  const m = Math.round((Date.now() - ms) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  return h < 24 ? `${h} h ago` : new Date(ms).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** Current state of the chip, for anything else that wants to show it. */
export function syncState() {
  const all = pendingWrites();
  const failed = all.filter(e => e.stuck);
  const waiting = all.filter(e => !e.stuck);
  const online = isFirebaseConnected();
  if (failed.length) return { key: 'failed', label: `${failed.length} not saved`, failed, waiting, online };
  if (!online) return { key: 'offline', label: `Offline${waiting.length ? ` · ${waiting.length} on device` : ''}`, failed, waiting, online };
  const n = inFlightWrites() + waiting.length;
  if (n) return { key: 'saving', label: `Saving ${n}`, failed, waiting, online, n };
  return { key: 'live', label: 'Live', failed, waiting, online };
}

/**
 * Mounts the chip into `host`. Options: theme 'light' | 'dark', popover placement
 * 'below' (default) | 'above' | 'right'. Returns { refresh, destroy }.
 */
export function mountSyncChip(host, { theme = 'light', place = 'below' } = {}) {
  if (!host) return { refresh() {}, destroy() {} };
  host.classList.add('sync-wrap', `sync-${theme}`, `sync-place-${place}`);
  host.innerHTML = `
    <button type="button" class="sync-chip" aria-haspopup="dialog" aria-expanded="false">
      <span class="sync-dot"></span><span class="sync-label">Live</span>
    </button>
    <div class="sync-pop" role="dialog" aria-label="Changes waiting to sync" hidden></div>`;
  const chip = host.querySelector('.sync-chip');
  const pop = host.querySelector('.sync-pop');
  let savingSince = 0;
  let savingTimer = null;
  let shownKey = 'live';

  function paint() {
    const st = syncState();
    // "Saving" only after it has taken more than a second — a quick save never flickers.
    let show = st;
    if (st.key === 'saving') {
      if (!savingSince) savingSince = Date.now();
      if (Date.now() - savingSince < SAVING_DELAY_MS) {
        show = shownKey === 'saving' ? st : { key: 'live', label: 'Live' };
        clearTimeout(savingTimer);
        savingTimer = setTimeout(paint, SAVING_DELAY_MS - (Date.now() - savingSince) + 20);
      }
    } else {
      savingSince = 0;
    }
    shownKey = show.key;
    chip.className = `sync-chip sync-${show.key}`;
    chip.querySelector('.sync-label').textContent = show.label;
    chip.title = show.key === 'live' ? 'Connected — everything is saved' : 'See what’s waiting';
    if (!pop.hidden) renderPop();
  }

  function renderPop() {
    const st = syncState();
    const rows = [...st.failed, ...st.waiting].sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
    const conflicts = st.failed.filter(e => e.conflict);
    const refused = st.failed.filter(e => !e.conflict);
    const show = v => (v == null || v === '' ? '(blank)' : typeof v === 'object' ? JSON.stringify(v).slice(0, 60) : String(v).slice(0, 60));
    const pairs = e => Object.keys(e.value || {}).map(k => `${k.replace(/_/g, ' ')}: theirs “${show(e.theirs?.[k])}”, yours “${show(e.value[k])}”`).join(' · ');
    const head = conflicts.length && !refused.length
      ? `<div class="sync-pop-title">${conflicts.length === 1 ? 'A change' : `${conflicts.length} changes`} made offline met someone else’s</div><div class="sync-pop-sub">While this device was offline, someone else changed the same ${conflicts.length === 1 ? 'thing' : 'things'}. Theirs was kept. Keep theirs, or use yours instead.</div>`
      : st.failed.length
      ? `<div class="sync-pop-title">${st.failed.length} ${st.failed.length === 1 ? 'change wasn’t' : 'changes weren’t'} saved</div><div class="sync-pop-sub">The server refused ${st.failed.length === 1 ? 'it' : 'them'} — usually a permission problem. Try again, or discard ${st.failed.length === 1 ? 'it' : 'them'}.</div>`
      : !st.online
        ? `<div class="sync-pop-title">Offline</div><div class="sync-pop-sub">${st.waiting.length ? `${st.waiting.length} ${st.waiting.length === 1 ? 'change is' : 'changes are'} kept on this device and will save when the connection is back.` : 'Nothing waiting. Changes you make are kept on this device until the connection is back.'}</div>`
        : `<div class="sync-pop-title">${st.key === 'saving' ? 'Saving…' : 'Everything is saved'}</div><div class="sync-pop-sub">${st.key === 'saving' ? 'Changes are on their way to the server.' : 'Connected. Changes save as you make them.'}</div>`;
    pop.innerHTML = head + (rows.length ? `<div class="sync-pop-list">${rows.slice(0, 30).map(e => `
      <div class="sync-row${e.stuck ? ' failed' : ''}">
        <div class="sync-row-main">
          <div class="sync-row-what">${escapeHTML(describe(e.path))}</div>
          <div class="sync-row-sub">${escapeHTML(ago(e.savedAt || Date.now()))}${e.stuck ? ' · not saved' : ' · waiting'}</div>
          ${e.conflict ? `<div class="sync-row-code">${escapeHTML(pairs(e))}</div>`
            : e.stuck && e.lastError ? `<div class="sync-row-code">${escapeHTML(String(e.lastError).slice(0, 80))} · ${escapeHTML(e.path)}</div>` : ''}
        </div>
        ${e.conflict ? `<button type="button" class="sync-row-btn" data-keep="${escapeHTML(e.id)}">Keep theirs</button><button type="button" class="sync-row-btn" data-mine="${escapeHTML(e.id)}">Use mine</button>`
          : e.stuck ? `<button type="button" class="sync-row-btn" data-discard="${escapeHTML(e.id)}">Discard</button>` : ''}
      </div>`).join('')}</div>` : '')
      + (refused.length ? `<div class="sync-pop-actions"><button type="button" class="sync-btn-primary" data-retry>Try again</button></div>` : '');
  }

  function open(on) {
    pop.hidden = !on;
    chip.setAttribute('aria-expanded', String(on));
    if (on) renderPop();
  }

  chip.addEventListener('click', (e) => { e.stopPropagation(); open(pop.hidden); });
  pop.addEventListener('click', async (e) => {
    e.stopPropagation();
    const keep = e.target.closest('[data-keep]');
    if (keep) { discardPendingWrite(keep.dataset.keep); return; }
    const mine = e.target.closest('[data-mine]');
    if (mine) {
      mine.textContent = 'Saving…';
      try { await useMinePendingWrite(mine.dataset.mine); } catch (err) { console.error(err); mine.textContent = 'Use mine'; }
      paint();
      return;
    }
    const d = e.target.closest('[data-discard]');
    if (d) {
      if (window.confirm('Discard this change? It will not be saved.')) discardPendingWrite(d.dataset.discard);
      return;
    }
    if (e.target.closest('[data-retry]')) {
      e.target.closest('[data-retry]').textContent = 'Trying…';
      await retryPendingWrites();
      paint();
    }
  });
  const onDoc = (e) => { if (!host.contains(e.target)) open(false); };
  const onKey = (e) => { if (e.key === 'Escape') open(false); };
  document.addEventListener('click', onDoc);
  document.addEventListener('keydown', onKey);
  const offConn = onFirebaseConnectedChange(paint);
  const offLog = onPendingWritesChange(paint);
  const tick = setInterval(() => { if (!pop.hidden) renderPop(); }, 30000);
  paint();

  return {
    refresh: paint,
    destroy() {
      document.removeEventListener('click', onDoc);
      document.removeEventListener('keydown', onKey);
      if (typeof offConn === 'function') offConn();
      if (typeof offLog === 'function') offLog();
      clearInterval(tick);
      clearTimeout(savingTimer);
    },
  };
}
